/**
 * Slug to Function Name Converter
 *
 * Converts problem slugs (e.g., "tree-check") to language-specific function names.
 * - Python/C++: snake_case (tree_check)
 * - JavaScript/Java: camelCase (treeCheck)
 * - Go: PascalCase (TreeCheck)
 */

/**
 * Languages supported for function name generation.
 * Note: This is broader than the test harness's SupportedLanguage type
 * because function naming conventions apply even to languages not yet
 * supported for code execution.
 */
export type FunctionNameLanguage =
  | 'python'
  | 'javascript'
  | 'java'
  | 'cpp'
  | 'go'

/**
 * Convert a problem slug to a function name following language conventions.
 *
 * @param slug - The problem slug (e.g., "tree-check", "two-sum")
 * @param language - The target programming language
 * @returns Function name following the language's naming convention
 *
 * @example
 * slugToFunctionName('tree-check', 'python') // => 'tree_check'
 * slugToFunctionName('tree-check', 'javascript') // => 'treeCheck'
 * slugToFunctionName('tree-check', 'go') // => 'TreeCheck'
 * slugToFunctionName('3sum', 'python') // => 'sum' (strips leading digits)
 */
export function slugToFunctionName(
  slug: string,
  language: FunctionNameLanguage,
): string {
  // Remove leading digits and dashes (e.g., "3sum" -> "sum")
  const cleaned = slug.replace(/^[0-9-]+/, '')

  // Split by hyphens and filter out empty parts
  const parts = cleaned.split('-').filter(Boolean)

  // Handle edge case: no valid parts after cleaning
  if (parts.length === 0) {
    throw new Error(
      `Cannot convert slug "${slug}" to function name: no valid parts after cleaning`,
    )
  }

  // Python and C++ use snake_case
  if (language === 'python' || language === 'cpp') {
    return parts.join('_')
  }

  // Go uses PascalCase (exported functions must start with uppercase)
  if (language === 'go') {
    return parts
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join('')
  }

  // JavaScript and Java use camelCase
  return parts
    .map((part, index) => {
      if (index === 0) {
        return part.toLowerCase()
      }
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    })
    .join('')
}

/**
 * Get the default function name for a problem based on slug and language.
 * Returns null if the slug is invalid or missing.
 *
 * @param slug - The problem slug
 * @param language - The target programming language
 * @returns Function name or null if invalid
 */
export function getDefaultFunctionName(
  slug: string | null | undefined,
  language: FunctionNameLanguage,
): string | null {
  if (!slug || typeof slug !== 'string') {
    return null
  }

  return slugToFunctionName(slug, language)
}
